const geojsonRandom = require('geojson-random'),
  geojsonExtent = require('@mapbox/geojson-extent'),
  geojsonFlatten = require('geojson-flatten'),
  polyline = require('@mapbox/polyline'),
  wkx = require('wkx'),
  Buffer = require('buffer/').Buffer,
  zoomextent = require('../lib/zoomextent'),
  openlr = require('openlr-js');

function isValidTileUrl(url) {
  try {
    const u = new URL(url);

    // Must use HTTPS
    if (u.protocol !== 'https:') return false;

    // Optional: check that {z}, {x}, {y} placeholders are present
    const hasPlaceholders =
      url.includes('{z}') && url.includes('{x}') && url.includes('{y}');
    if (!hasPlaceholders) return false;

    return true;
  } catch (e) {
    // Invalid URL format
    return false;
  }
}

function isValidTilesetName(name) {
  const trimmed = name.trim();
  return (
    /^[a-zA-Z0-9 ]+$/.test(trimmed) &&
    trimmed.length >= 3 &&
    trimmed.length <= 25
  );
}

// Validate that GeoJSON coordinates are within reasonable geographic bounds
// Returns { valid: boolean, message: string }
function validateGeoJSONCoordinates(geojson) {
  const MAX_LNG = 180;
  const MAX_LAT = 90;

  function checkCoord(coord) {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const [lng, lat] = coord;
    return (
      typeof lng === 'number' &&
      typeof lat === 'number' &&
      isFinite(lng) &&
      isFinite(lat) &&
      Math.abs(lng) <= MAX_LNG &&
      Math.abs(lat) <= MAX_LAT
    );
  }

  function checkCoords(coords, depth) {
    if (depth === 0) {
      return checkCoord(coords);
    }
    if (!Array.isArray(coords)) return false;
    return coords.every((c) => checkCoords(c, depth - 1));
  }

  function checkGeometry(geom) {
    if (!geom || !geom.type) return true; // null geometry is ok
    switch (geom.type) {
      case 'Point':
        return checkCoords(geom.coordinates, 0);
      case 'LineString':
      case 'MultiPoint':
        return checkCoords(geom.coordinates, 1);
      case 'Polygon':
      case 'MultiLineString':
        return checkCoords(geom.coordinates, 2);
      case 'MultiPolygon':
        return checkCoords(geom.coordinates, 3);
      case 'GeometryCollection':
        return (geom.geometries || []).every(checkGeometry);
      default:
        return true;
    }
  }

  if (geojson.type === 'FeatureCollection') {
    const valid = (geojson.features || []).every(
      (f) => !f.geometry || checkGeometry(f.geometry)
    );
    if (!valid) {
      return {
        valid: false,
        message:
          'Coordinates are outside valid geographic bounds (longitude: -180 to 180, latitude: -90 to 90).'
      };
    }
  } else if (geojson.type === 'Feature') {
    if (geojson.geometry && !checkGeometry(geojson.geometry)) {
      return {
        valid: false,
        message:
          'Coordinates are outside valid geographic bounds (longitude: -180 to 180, latitude: -90 to 90).'
      };
    }
  } else {
    // It's a geometry object directly
    if (!checkGeometry(geojson)) {
      return {
        valid: false,
        message:
          'Coordinates are outside valid geographic bounds (longitude: -180 to 180, latitude: -90 to 90).'
      };
    }
  }

  return { valid: true, message: '' };
}

module.exports.adduserlayer = function (context, url, name) {
  function addUserSourceAndLayer() {
    // Remove existing user layer if present
    if (context.map.getLayer('user-raster-layer')) {
      context.map.removeLayer('user-raster-layer');
    }
    if (context.map.getSource('user-raster-source')) {
      context.map.removeSource('user-raster-source');
    }

    // Add the raster source
    context.map.addSource('user-raster-source', {
      type: 'raster',
      tiles: [url],
      tileSize: 256
    });

    // Find the first symbol layer to insert the raster below labels
    const layers = context.map.getStyle().layers;
    let firstSymbolId;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i].type === 'symbol') {
        firstSymbolId = layers[i].id;
        break;
      }
    }

    // Add the raster layer below labels
    context.map.addLayer(
      {
        id: 'user-raster-layer',
        type: 'raster',
        source: 'user-raster-source',
        paint: { 'raster-opacity': 1 },
        minzoom: 0,
        maxzoom: 22
      },
      firstSymbolId
    );

    // make this layer's button active
    d3.select('.layer-switch .active').classed('active', false);
    d3.select('.user-layer-button').classed('active', true);

    context.data.set({
      mapStyleLoaded: true
    });
  }

  try {
    if (!isValidTileUrl(url)) {
      throw new Error(
        'Invalid tile URL. Must be HTTPS and include {z}, {x}, {y}.'
      );
    }

    if (!isValidTilesetName(name)) {
      throw new Error(
        'Invalid tileset name. Must be 3-25 characters long and contain only alphanumeric characters and spaces.'
      );
    }

    // reset the control if a user-layer was added before
    d3.select('.user-layer-button').remove();

    // append a button to the existing style selection UI
    d3.select('.layer-switch')
      .append('button')
      .attr('class', 'pad0x user-layer-button')
      .on('click', addUserSourceAndLayer)
      .text(name);

    addUserSourceAndLayer();
  } catch (e) {
    alert(e.message);
  }
};

module.exports.zoomextent = function (context) {
  zoomextent(context);
};

module.exports.clear = function (context) {
  context.data.clear();
};

module.exports.random = function (context, count, type) {
  context.data.mergeFeatures(geojsonRandom(count, type).features, 'meta');
};

module.exports.bboxify = function (context) {
  context.data.set({ map: geojsonExtent.bboxify(context.data.get('map')) });
};

module.exports.flatten = function (context) {
  context.data.set({ map: geojsonFlatten(context.data.get('map')) });
};

module.exports.polyline = function (context) {
  const input = prompt('Enter your polyline');
  try {
    const decoded = polyline.toGeoJSON(input);
    context.data.set({ map: decoded });
  } catch (e) {
    alert('Sorry, we were unable to decode that polyline');
  }
};

module.exports.polyline6 = function (context) {
  const input = prompt('Enter your polyline');
  try {
    const decoded = polyline.toGeoJSON(input, 6);
    context.data.set({ map: decoded });
  } catch (e) {
    alert('Sorry, we were unable to decode that polyline');
  }
};

module.exports.wkxBase64 = function (context) {
  const input = prompt('Enter your Base64 encoded WKB/EWKB');
  if (!input || !input.trim()) {
    return;
  }

  const trimmedInput = input.trim();

  // Validate base64 format (standard base64 characters + optional padding)
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(trimmedInput)) {
    alert(
      'Invalid Base64 format. Please ensure the string contains only valid Base64 characters (A-Z, a-z, 0-9, +, /, =).'
    );
    return;
  }

  // Check minimum length for valid WKB (at least 5 bytes: 1 endian + 4 type)
  let buffer;
  try {
    buffer = Buffer.from(trimmedInput, 'base64');
  } catch (e) {
    alert('Invalid Base64 encoding. Could not decode the string.');
    return;
  }

  if (buffer.length < 5) {
    alert(
      'Invalid WKB data. The decoded data is too short to be a valid WKB geometry (minimum 5 bytes required).'
    );
    return;
  }

  // Validate WKB endianness byte (must be 0 for big-endian or 1 for little-endian)
  const endianness = buffer[0];
  if (endianness !== 0 && endianness !== 1) {
    alert(
      'Invalid WKB data. The first byte must be 0 (big-endian) or 1 (little-endian), but got ' +
        endianness +
        '.'
    );
    return;
  }

  try {
    const decoded = wkx.Geometry.parse(buffer);
    const geojson = decoded.toGeoJSON();

    // Validate coordinates are within geographic bounds
    const coordValidation = validateGeoJSONCoordinates(geojson);
    if (!coordValidation.valid) {
      alert('Invalid WKB data: ' + coordValidation.message);
      return;
    }

    context.data.set({ map: geojson });
    zoomextent(context);
  } catch (e) {
    console.error(e);
    let errorMsg =
      'Sorry, we were unable to decode that Base64 encoded WKB data.';
    if (
      e.message &&
      (e.message.includes('beyond buffer length') ||
        e.message.includes('out of range'))
    ) {
      errorMsg +=
        ' The data appears to be truncated or incomplete. Please verify the full WKB data was copied.';
    }
    alert(errorMsg);
  }
};

module.exports.wkxHex = function (context) {
  const input = prompt('Enter your Hex encoded WKB/EWKB');
  if (!input || !input.trim()) {
    return;
  }
  try {
    const decoded = wkx.Geometry.parse(Buffer.from(input.trim(), 'hex'));
    const geojson = decoded.toGeoJSON();

    // Validate coordinates are within geographic bounds
    const coordValidation = validateGeoJSONCoordinates(geojson);
    if (!coordValidation.valid) {
      alert('Invalid WKB data: ' + coordValidation.message);
      return;
    }

    context.data.set({ map: geojson });
    zoomextent(context);
  } catch (e) {
    console.error(e);
    alert('Sorry, we were unable to decode that Hex encoded WKB data');
  }
};

module.exports.wkxString = function (context) {
  const input = prompt('Enter your WKT/EWKT String');
  if (!input || !input.trim()) {
    return;
  }
  try {
    // Strip SRID prefix from EWKT (e.g., "SRID=4326;POINT(1 2)" -> "POINT(1 2)")
    let wktString = input.trim();
    if (wktString.toUpperCase().startsWith('SRID=')) {
      const semicolonIndex = wktString.indexOf(';');
      if (semicolonIndex !== -1) {
        wktString = wktString.substring(semicolonIndex + 1).trim();
      }
    }
    const decoded = wkx.Geometry.parse(wktString);
    const geojson = decoded.toGeoJSON();

    // Validate coordinates are within geographic bounds
    const coordValidation = validateGeoJSONCoordinates(geojson);
    if (!coordValidation.valid) {
      alert('Invalid WKT data: ' + coordValidation.message);
      return;
    }

    context.data.set({ map: geojson });
    zoomextent(context);
  } catch (e) {
    console.error(e);
    alert(
      'Sorry, we were unable to decode that WKT data. Please check the format is valid (e.g., POINT(1 2), LINESTRING(0 0, 1 1)).'
    );
  }
};

module.exports.openLR = function (context) {
  const openLrInput = prompt(
    'Enter your OpenLR String(s) - comma separated for multiple'
  );
  try {
    // Split by comma and trim whitespace
    const openLrStrings = openLrInput
      .split(',')
      .map((s) => s.trim().trim('"'))
      .filter((s) => s.length > 0);

    const BinaryDecoder = openlr.BinaryDecoder;
    const binaryDecoder = new BinaryDecoder();
    const LocationReference = openlr.LocationReference;
    const Serializer = openlr.Serializer;

    const allFeatures = [];
    const errors = [];

    // Process each OpenLR string
    for (const openLrString of openLrStrings) {
      try {
        const openLrBinary = Buffer.from(openLrString, 'base64');
        const locationReference = LocationReference.fromIdAndBuffer(
          'binary',
          openLrBinary
        );
        const rawLocationReference =
          binaryDecoder.decodeData(locationReference);
        const jsonObject = Serializer.serialize(rawLocationReference);

        switch (jsonObject.type) {
          case 'RawLineLocationReference':
            {
              const coordinates = jsonObject.properties._points.properties.map(
                ({ properties }) => [
                  properties._longitude,
                  properties._latitude
                ]
              );
              allFeatures.push({
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: coordinates
                },
                properties: {
                  raw: jsonObject,
                  input: openLrString
                }
              });
            }
            break;
          case 'RawGeoCoordLocationReference':
            {
              const point = jsonObject.properties._geoCoord.properties;
              allFeatures.push({
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [point._longitude, point._latitude]
                },
                properties: {
                  raw: jsonObject,
                  input: openLrString
                }
              });
            }
            break;
          case 'RawPolygonLocationReference':
            {
              const polygonCorners = jsonObject.properties._corners.properties;
              const polygonCoordinates = polygonCorners.map((point) => [
                point.properties._longitude,
                point.properties._latitude
              ]);
              allFeatures.push({
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [[...polygonCoordinates]]
                },
                properties: {
                  raw: jsonObject,
                  input: openLrString
                }
              });
            }
            break;
          default:
            errors.push(
              `Unsupported OpenLR location type: ${jsonObject.type} for input: ${openLrString}`
            );
        }
      } catch (e) {
        errors.push(`Failed to decode: ${openLrString.substring(0, 20)}...`);
        console.error(`Error decoding ${openLrString}:`, e);
      }
    }

    if (allFeatures.length > 0) {
      const geojson = {
        type: 'FeatureCollection',
        features: allFeatures
      };
      context.data.set({ map: geojson });
      zoomextent(context);

      if (errors.length > 0) {
        alert(
          `Successfully decoded ${
            allFeatures.length
          } features, but encountered ${errors.length} error(s):\n${errors.join(
            '\n'
          )}`
        );
      }
    } else {
      alert(
        'Sorry, we were unable to decode any OpenLR data:\n' + errors.join('\n')
      );
    }
  } catch (e) {
    console.error(e);
    alert('Sorry, we were unable to decode that OpenLR data');
  }
};
